// @ts-nocheck
import React, { useCallback, useMemo } from 'react';

import { useDebouncedInput } from '../../../../hooks/useDebouncedInput';
import { IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { ReactComponent as LockIcon } from '../../../../assets/Icons/lucideIcons/lock.svg';

import { ReactComponent as CalculationIcon } from '../../../../assets/images/CalculationIcon.svg';
import { ReactComponent as OpenExternalLinkIcon } from '../../../../assets/Icons/lucideIcons/square-arrow-out-up-right.svg';

import { getErrorHelperText } from '../../../../utils/Utils';
import { getCharacterLimitForAttribute } from '../../../../utils/formUtils';
import { composeDraftBlurHandler } from '../../../../hooks/useDraftAutoSave.jsx';
import { NOOP } from '../../../../const/CommonConst';
import REGEX from '../../../../const/Regex';
import {
  DRAFT_INPUT_SX,
  NEW_VARIANT_EXTERNAL_LABEL_INPUT_SX,
} from './DraftFieldExtras';

export { DRAFT_INPUT_SX };

// System attribute shown as "ID"; its value is restricted to REGEX.VALID_ENTITY_CODE.
const ENTITY_CODE_ATTRIBUTE_NAME = 'entity_code';

export const INPUT_LABEL_PROPS = {
  shrink: true,
  sx: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },
};

/**
 * Text field component for editing text attributes
 * @param {Object} props - Component props
 * @param {string} props.label - Field label
 * @param {string|number} props.value - Current field value
 * @param {Function} props.onChange - Function to handle value changes
 * @param {Object} props.attribute - Attribute configuration object
 * @param {Object} props.entityDetailsFormikForm - Formik form instance
 * @param {string} props.titleLabel - Title label for the field
 * @param {React.ReactElement} props.endAdornment - Custom end adornment element (if provided, overrides default behavior)
 * @param {Function} props.onClick - Optional click handler for the TextField
 * @param {Object} props.sx - Optional sx prop for custom styling
 * @param {string} props.customPlaceholder - Optional custom placeholder text
 * @returns {React.ReactElement} The TextField component
 */
const TextFieldComponent = ({
  label,
  value,
  onChange,
  attribute,
  entityDetailsFormikForm,
  titleLabel,
  required = false,
  handleOpenDerivedModal,
  hideHeader,
  isLoadingConstraints = false,
  configColumn = false,
  endAdornment = null,
  onClick = null,
  sx = {},
  customPlaceholder = null,
  hasDraftChip = false,
  onDraftBlur,
}) => {
  const resolvedSx = useMemo(() => {
    if (hasDraftChip) return [sx, DRAFT_INPUT_SX];
    if (!label) return [sx, NEW_VARIANT_EXTERNAL_LABEL_INPUT_SX];
    return sx;
  }, [hasDraftChip, label, sx]);

  const isDisabled = !attribute?.is_editable;
  const isDerived = hideHeader
    ? attribute?.is_derived && attribute?.permission === 'manage'
    : attribute?.is_derived;

  const maxLength = useMemo(
    () => getCharacterLimitForAttribute(attribute),
    [attribute]
  );

  const syncedValue = useMemo(() => {
    const attrId = attribute?.id;
    const formikVal =
      entityDetailsFormikForm?.values?.[attrId] ??
      entityDetailsFormikForm?.values?.[Number(attrId)];

    // Modal triggers (Table, AdvancedTable, etc.) pass a pre-formatted string
    // via `value` while Formik stores the raw object — prefer the display string.
    const resolved =
      onChange === NOOP && typeof value === 'string'
        ? value
        : (formikVal ?? value);

    return resolved === undefined || resolved === null ? '' : String(resolved);
  }, [value, onChange, attribute?.id, entityDetailsFormikForm?.values]);

  const { localValue, setValue, flush } = useDebouncedInput(
    syncedValue,
    onChange
  );

  const errorHelperText = useMemo(
    () =>
      getErrorHelperText(
        entityDetailsFormikForm?.errors?.[attribute?.id],
        entityDetailsFormikForm?.touched?.[attribute?.id]
      ),
    [
      entityDetailsFormikForm?.errors,
      entityDetailsFormikForm?.touched,
      attribute?.id,
    ]
  );

  const hasError = useMemo(() => Boolean(errorHelperText), [errorHelperText]);

  // character count
  const characterCount = useMemo(() => localValue?.length || 0, [localValue]);

  // True => user has reached limit
  const atLimit = useMemo(
    () => maxLength !== undefined && characterCount >= maxLength,
    [maxLength, characterCount]
  );

  const helperTextContent = useMemo(() => {
    if (hasError) return errorHelperText;
    if (atLimit) {
      return `Maximum character limit reached (${maxLength}/${maxLength})`;
    }
    return '';
  }, [hasError, errorHelperText, atLimit, maxLength]);

  /**
   * Handles input change events
   * @function
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event
   * @returns {void}
   */
  const { onBlur: formikBlur, ...formikFieldProps } =
    entityDetailsFormikForm?.getFieldProps(attribute?.id) || {};

  const handleChange = useCallback(
    (e) => {
      const newValue = e.target.value;
      //block input once the character limit is reached
      if (maxLength !== undefined && newValue?.length > maxLength) {
        return;
      }
      // The ID (entity_code) attribute only accepts letters, digits, hyphen
      // and underscore, so ignore any change that would add anything else.
      if (
        attribute?.name === ENTITY_CODE_ATTRIBUTE_NAME &&
        !REGEX.VALID_ENTITY_CODE.test(newValue)
      ) {
        return;
      }
      setValue(newValue);
    },
    [setValue, maxLength, attribute?.name]
  );

  const handleBlur = useCallback(
    (event) => {
      flush();
      composeDraftBlurHandler(formikBlur, onDraftBlur)(event);
    },
    [flush, formikBlur, onDraftBlur]
  );

  const handleClickAttribute = useCallback(
    (e) => {
      e.stopPropagation();
      handleOpenDerivedModal(attribute);
    },
    [handleOpenDerivedModal, attribute]
  );

  const handleOpenHyperlink = useCallback(() => {
    if (localValue) {
      window.open(localValue, '_blank', 'noopener,noreferrer');
    }
  }, [localValue]);

  const inputPropsConfig = useMemo(() => {
    // If custom endAdornment is provided via props, use it directly
    if (endAdornment !== null) {
      return { endAdornment };
    }

    // Otherwise, generate default adornments based on attribute state
    let defaultEndAdornment = null;

    if (isDerived) {
      defaultEndAdornment = (
        <InputAdornment position="end">
          <Tooltip
            title={
              !attribute?.show_calculation_popUp
                ? 'Enable the calculation popup to calculate this attribute.'
                : `Calculate ${attribute?.label}`
            }
          >
            <IconButton
              sx={{ p: 0 }}
              className="disabled-svg-icon"
              onClick={handleClickAttribute}
              disabled={!attribute?.show_calculation_popUp}
            >
              <CalculationIcon />
            </IconButton>
          </Tooltip>
        </InputAdornment>
      );
    } else if (!attribute?.is_editable) {
      defaultEndAdornment = (
        <InputAdornment position="end">
          <Tooltip title="Field is locked or not editable">
            <LockIcon />
          </Tooltip>
        </InputAdornment>
      );
    } else if (attribute?.data_type === 'Hyperlink') {
      defaultEndAdornment = (
        <InputAdornment position="end">
          <IconButton
            type="button"
            disabled={!localValue}
            onClick={handleOpenHyperlink}
          >
            <OpenExternalLinkIcon width={16} height={16} />
          </IconButton>
        </InputAdornment>
      );
    }

    return { endAdornment: defaultEndAdornment };
  }, [
    endAdornment,
    attribute?.is_derived,
    attribute?.is_editable,
    attribute?.show_calculation_popUp,
    attribute?.label,
    attribute?.data_type,
    localValue,
    handleClickAttribute,
    handleOpenHyperlink,
  ]);

  const handleKeyDown = useCallback(
    (e) => {
      if (configColumn && e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
      }
    },
    [configColumn]
  );

  return (
    <TextField
      {...(onChange !== NOOP ? formikFieldProps : {})}
      type={configColumn ? undefined : 'text'}
      multiline={configColumn}
      fullWidth
      title={typeof label === 'string' ? titleLabel : undefined}
      label={label}
      placeholder={customPlaceholder || (isDerived ? '-' : 'Enter a value')}
      value={localValue}
      disabled={isDisabled || isDerived}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      variant="outlined"
      size="medium"
      spellCheck
      required={required}
      slotProps={{
        input: {
          ...inputPropsConfig,
          readOnly: isLoadingConstraints,
          sx: {
            paddingRight: '14px !important',
            '& .MuiInputAdornment-positionEnd': {
              marginLeft: '8px',
              marginRight: '0px',
            },
          },
        },
        htmlInput: {
          ...(maxLength !== undefined ? { maxLength } : {}),
        },
        inputLabel: INPUT_LABEL_PROPS,
        formHelperText: {
          sx: {
            color: !hasError && atLimit ? 'warning.main' : undefined,
          },
        },
      }}
      error={hasError}
      helperText={helperTextContent}
      onClick={onClick}
      sx={resolvedSx}
    />
  );
};

TextFieldComponent.propTypes = {
  label: PropTypes.string.isRequired,
  titleLabel: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  attribute: PropTypes.shape({
    name: PropTypes.string,
    data_type: PropTypes.string,
    is_editable: PropTypes.bool,
    is_derived: PropTypes.bool,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    permission: PropTypes.string,
    show_calculation_popUp: PropTypes.bool,
  }).isRequired,
  entityDetailsFormikForm: PropTypes.shape({
    getFieldProps: PropTypes.func.isRequired,
    errors: PropTypes.objectOf(PropTypes.string).isRequired,
    touched: PropTypes.objectOf(PropTypes.bool).isRequired,
    values: PropTypes.objectOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool])
    ),
  }).isRequired,
  handleOpenDerivedModal: PropTypes.func,
  hideHeader: PropTypes.bool.isRequired,
  isLoadingConstraints: PropTypes.bool,
  configColumn: PropTypes.bool,
  endAdornment: PropTypes.node,
  onClick: PropTypes.func,
  sx: PropTypes.shape({}),
  customPlaceholder: PropTypes.string,
  hasDraftChip: PropTypes.bool,
  onDraftBlur: PropTypes.func,
};
export default React.memo(TextFieldComponent);
